import { NOTIFICATION_ROUTES } from '../config/notificationRoutes';
import { compileRoute } from './routeCompiler';
import { encodeQuotationId } from '../../modules/dashboard/quotations/utils/idEncoder';

function firstId(payload, keys) {
  for (const key of keys) {
    const value = payload?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return String(value);
    }
  }
  return '';
}

function eventKeys(normalizedEvent) {
  if (!normalizedEvent) return [];
  return [
    normalizedEvent,
    normalizedEvent.replace(/s$/, ''),
    `${normalizedEvent}s`,
    normalizedEvent.replace(/_notification$/, ''),
    normalizedEvent.replace(/_alert$/, ''),
    normalizedEvent.replace(/_event$/, ''),
  ];
}

function matchesEvent(normalizedEvent, predicate) {
  return eventKeys(normalizedEvent).some(predicate);
}

function resolveQuotationPath(payload) {
  const id = firstId(payload, ['quotation_id', 'quotationId', 'id']);
  return id ? `/quotations/${encodeURIComponent(id)}` : '/quotations';
}

function resolveOrderPath(payload) {
  const id = firstId(payload, [
    'customer_quotation_id',
    'customerQuotationId',
    'order_id',
    'orderId',
  ]);
  if (!id) return '/quotations?tab=orders';
  return `/quotations/customer/${encodeQuotationId(id)}`;
}

function resolveCustomerPath(payload) {
  const contactId = firstId(payload, [
    'contact_id',
    'contactId',
    'customer_id',
    'customerId',
    'client_id',
    'clientId',
  ]);
  if (!contactId) return '/crm-contacts';
  const compiled = compileRoute(
    '/crm-contacts/overview?contact_id=:contact_id',
    { ...payload, contact_id: contactId },
  );
  if (
    !compiled ||
    compiled === '/' ||
    (compiled.startsWith('/crm-contacts/overview') &&
      !compiled.includes('contact_id='))
  ) {
    return '/crm-contacts';
  }
  return compiled;
}

const INTEGRATION_EVENTS = new Set([
  'integration_connected',
  'integration_disconnected',
  'integration_unhealthy',
  'integration_reconnected',
  'integration_connection',
  'integration_failed',
  'token_expired',
  'authentication_failed',
  'permissions_revoked',
]);

function rewriteLaunchPath(path) {
  const q = path.indexOf('?');
  const pathname =
    (q === -1 ? path : path.slice(0, q)).replace(/\/+$/, '') || '/';
  const search = q === -1 ? '' : path.slice(q);
  if (pathname === '/inbox') return `/whatsapp-chat${search}`;
  return path;
}

function resolveInboxPath(payload) {
  const channel = String(payload?.channel || '')
    .trim()
    .toLowerCase();
  const conversationId = firstId(payload, [
    'conversation_id',
    'conversationId',
    'chat_id',
  ]);
  const params = new URLSearchParams();
  if (channel === 'whatsapp' || channel === 'instagram') {
    params.set('channel', channel);
  }
  if (conversationId) params.set('conversation_id', conversationId);
  const qs = params.toString();
  return qs ? `/whatsapp-chat?${qs}` : '/whatsapp-chat';
}

function resolveCrmActivityPath(payload) {
  const contactId = firstId(payload, ['contact_id', 'contactId']);
  if (!contactId) return '/crm-contacts';
  const taskId = firstId(payload, ['task_id', 'taskId']);
  const tab = String(payload?.tab || '').trim();
  if (taskId || tab === 'tasks') {
    const params = new URLSearchParams({ contact_id: contactId, tab: 'tasks' });
    if (taskId) params.set('task_id', taskId);
    return `/crm-contacts/overview?${params.toString()}`;
  }
  return `/crm-contacts/overview?contact_id=${encodeURIComponent(contactId)}`;
}

function resolveAppointmentPath(payload) {
  const id = firstId(payload, ['appointment_id', 'appointmentId']);
  if (!id) return '/calendar';
  return (
    compileRoute('/calendar?appointment_id=:appointment_id', {
      ...payload,
      appointment_id: id,
    }) || '/calendar'
  );
}

function resolveTypedEntityPath(normalizedEvent, payload) {
  if (normalizedEvent === 'inbox_message') return resolveInboxPath(payload);
  if (normalizedEvent === 'crm_activity')
    return resolveCrmActivityPath(payload);
  if (INTEGRATION_EVENTS.has(normalizedEvent)) return '/integrations';
  if (
    matchesEvent(
      normalizedEvent,
      (key) =>
        key === 'appointment' ||
        key.startsWith('appointment_') ||
        key === 'booking' ||
        key.startsWith('booking_'),
    )
  ) {
    return resolveAppointmentPath(payload);
  }
  if (
    matchesEvent(
      normalizedEvent,
      (key) =>
        key === 'order' ||
        key.startsWith('order_') ||
        key.startsWith('customer_quotation'),
    )
  ) {
    return resolveOrderPath(payload);
  }
  if (
    matchesEvent(
      normalizedEvent,
      (key) => key === 'quotation' || key.startsWith('quotation_'),
    )
  ) {
    return resolveQuotationPath(payload);
  }
  if (
    matchesEvent(
      normalizedEvent,
      (key) => key === 'session' || key.startsWith('session_'),
    )
  ) {
    return '/whatsapp-chat';
  }
  if (
    matchesEvent(
      normalizedEvent,
      (key) => key === 'customer' || key.startsWith('customer_'),
    )
  ) {
    return resolveCustomerPath(payload);
  }
  return null;
}

export function getNotificationParentPath(resolvedPath) {
  if (!resolvedPath) return '/dashboard';
  const pathname = resolvedPath.split('?')[0];

  if (pathname.startsWith('/reminders')) {
    return '/reminders';
  }
  if (pathname.startsWith('/crm-contacts')) {
    return '/crm-contacts';
  }
  if (pathname.startsWith('/crm-pipeline')) {
    return '/crm-pipeline';
  }
  if (pathname.startsWith('/crm-segments')) {
    return '/crm-segments';
  }
  if (pathname.startsWith('/crm-settings')) {
    return '/crm-settings';
  }
  if (pathname.startsWith('/crm-dashboard')) {
    return '/crm-dashboard';
  }
  if (pathname.startsWith('/whatsapp-chat')) {
    return '/whatsapp-chat';
  }
  if (pathname.startsWith('/whatsapp-flows')) {
    return '/whatsapp-flows';
  }
  if (pathname.startsWith('/campaigns')) {
    return '/campaigns';
  }
  if (pathname.startsWith('/quotations')) {
    return '/quotations';
  }
  if (pathname.startsWith('/templates')) {
    return '/templates';
  }
  if (pathname.startsWith('/calendar')) {
    return '/calendar';
  }
  if (pathname.startsWith('/employees')) {
    return '/employees';
  }
  if (pathname.startsWith('/integrations')) {
    return '/integrations';
  }
  if (pathname.startsWith('/settings')) {
    return '/settings';
  }
  if (pathname.startsWith('/profile')) {
    return '/profile';
  }

  return '/dashboard';
}

export function resolveNotificationRoute(notification) {
  if (!notification) return '/dashboard';

  // 1. Direct URL/Link from notification
  const directLink =
    notification.route ||
    notification.link ||
    notification.redirect_url ||
    notification.url ||
    notification.action_url ||
    notification.target_url;

  if (typeof directLink === 'string' && directLink.trim()) {
    const trimmed = directLink.trim();
    if (trimmed.startsWith('/')) {
      return rewriteLaunchPath(trimmed);
    }
    try {
      const parsed = new URL(trimmed, window.location.origin);
      if (parsed.origin === window.location.origin) {
        return parsed.pathname + parsed.search + parsed.hash;
      }
    } catch {
      if (!trimmed.includes('://')) {
        return `/${trimmed}`;
      }
    }
  }

  // 2. Extract and parse payload
  let payload = notification.payload;
  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload);
    } catch {
      payload = {};
    }
  }
  if (!payload || typeof payload !== 'object') {
    payload = {};
  }

  // Check direct URL inside payload
  const payloadLink =
    payload.route ||
    payload.link ||
    payload.redirect_url ||
    payload.url ||
    payload.action_url ||
    payload.path;

  if (typeof payloadLink === 'string' && payloadLink.trim()) {
    const trimmed = payloadLink.trim();
    if (trimmed.startsWith('/')) {
      return rewriteLaunchPath(trimmed);
    }
    try {
      const parsed = new URL(trimmed, window.location.origin);
      if (parsed.origin === window.location.origin) {
        return parsed.pathname + parsed.search + parsed.hash;
      }
    } catch {
      if (!trimmed.includes('://')) {
        return `/${trimmed}`;
      }
    }
  }

  // Merge extra data if present
  let extraData =
    notification.data || notification.metadata || notification.extra_data || {};
  if (typeof extraData === 'string') {
    try {
      extraData = JSON.parse(extraData);
    } catch {
      extraData = {};
    }
  }
  const mergedPayload = { ...notification, ...extraData, ...payload };

  // 3. Determine eventType key
  const rawEventType =
    payload.event ||
    payload.type ||
    payload.notification_type ||
    notification.notification_type ||
    notification.type ||
    notification.event ||
    notification.entity_type ||
    payload.entity_type ||
    '';

  // Normalize eventType: lowercase, replace non-alphanumeric chars with underscore
  const normalizedEvent = String(rawEventType)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '');

  const typedPath = resolveTypedEntityPath(normalizedEvent, mergedPayload);
  if (typedPath) return typedPath;

  let routePattern = null;

  if (normalizedEvent && NOTIFICATION_ROUTES[normalizedEvent]) {
    routePattern = NOTIFICATION_ROUTES[normalizedEvent];
  } else if (normalizedEvent) {
    // Try singular / plural / suffix variations
    const possibleKeys = [
      normalizedEvent,
      normalizedEvent.replace(/s$/, ''),
      `${normalizedEvent}s`,
      normalizedEvent.replace(/_notification$/, ''),
      normalizedEvent.replace(/_alert$/, ''),
      normalizedEvent.replace(/_event$/, ''),
    ];
    for (const key of possibleKeys) {
      if (NOTIFICATION_ROUTES[key]) {
        routePattern = NOTIFICATION_ROUTES[key];
        break;
      }
    }
  }

  // 4. Keyword / Heuristic analysis if no exact pattern matched
  if (!routePattern) {
    const title = String(
      notification.title || payload.title || '',
    ).toLowerCase();
    const message = String(
      notification.message ||
        notification.body ||
        payload.message ||
        payload.body ||
        '',
    ).toLowerCase();
    const fullText = `${normalizedEvent} ${title} ${message}`;

    if (fullText.includes('order') || fullText.includes('customer quotation')) {
      return resolveOrderPath(mergedPayload);
    } else if (fullText.includes('quotation')) {
      return resolveQuotationPath(mergedPayload);
    } else if (/\bsession\b/.test(fullText)) {
      return '/whatsapp-chat';
    } else if (fullText.includes('reminder') || fullText.includes('remind')) {
      if (
        fullText.includes('import') ||
        fullText.includes('upload') ||
        fullText.includes('job') ||
        fullText.includes('csv') ||
        fullText.includes('excel') ||
        fullText.includes('file')
      ) {
        if (
          mergedPayload.job_id ||
          mergedPayload.jobId ||
          mergedPayload.job ||
          mergedPayload.id
        ) {
          routePattern = '/reminders/import/:job_id';
        } else {
          routePattern = '/reminders/import';
        }
      } else {
        routePattern = '/reminders';
      }
    } else if (
      fullText.includes('chat') ||
      fullText.includes('message') ||
      fullText.includes('inbox') ||
      fullText.includes('conversation')
    ) {
      routePattern = '/whatsapp-chat/:chat_id';
    } else if (
      fullText.includes('campaign') ||
      fullText.includes('broadcast') ||
      fullText.includes('bulk')
    ) {
      if (mergedPayload.campaign_id || mergedPayload.campaignId) {
        routePattern = '/campaigns/:campaign_id';
      } else {
        routePattern = '/campaigns';
      }
    } else if (fullText.includes('template')) {
      routePattern = '/templates';
    } else if (
      fullText.includes('flow') ||
      fullText.includes('bot') ||
      fullText.includes('automation')
    ) {
      routePattern = '/whatsapp-flows';
    } else if (
      fullText.includes('appointment') ||
      fullText.includes('calendar') ||
      fullText.includes('booking') ||
      fullText.includes('schedule')
    ) {
      if (fullText.includes('availability')) {
        routePattern = '/calendar/availability';
      } else {
        routePattern = '/calendar';
      }
    } else if (
      fullText.includes('contact') ||
      fullText.includes('lead') ||
      fullText.includes('client') ||
      fullText.includes('customer') ||
      fullText.includes('pipeline')
    ) {
      if (fullText.includes('import')) {
        routePattern = '/crm-contacts';
      } else if (fullText.includes('pipeline') || fullText.includes('stage')) {
        routePattern =
          '/crm-pipeline?contact_id=:contact_id&stage_id=:stage_id';
      } else if (fullText.includes('note')) {
        routePattern =
          '/crm-contacts/overview?contact_id=:contact_id&tab=notes&note_id=:note_id';
      } else if (fullText.includes('task')) {
        routePattern =
          '/crm-contacts/overview?contact_id=:contact_id&tab=tasks&task_id=:task_id';
      } else if (
        mergedPayload.contact_id ||
        mergedPayload.clientId ||
        mergedPayload.lead_id
      ) {
        routePattern = '/crm-contacts/overview?contact_id=:contact_id';
      } else {
        routePattern = '/crm-contacts';
      }
    } else if (
      fullText.includes('employee') ||
      fullText.includes('member') ||
      fullText.includes('team') ||
      fullText.includes('staff')
    ) {
      routePattern = '/employees';
    } else if (
      fullText.includes('integration') ||
      fullText.includes('webhook') ||
      fullText.includes('reconnect')
    ) {
      routePattern = '/integrations/:integration_id';
    } else if (fullText.includes('segment') || fullText.includes('audience')) {
      routePattern = '/crm-segments';
    } else if (
      fullText.includes('tag') ||
      fullText.includes('custom field') ||
      fullText.includes('auto assign')
    ) {
      routePattern = '/crm-settings';
    } else if (fullText.includes('feedback')) {
      routePattern = '/settings/feedback';
    } else if (fullText.includes('profile') || fullText.includes('password')) {
      routePattern = '/profile';
    } else if (fullText.includes('setting')) {
      routePattern = '/settings';
    } else {
      routePattern = '/dashboard';
    }
  }

  // 5. Compile the route with payload variables
  let resolvedPath = compileRoute(routePattern, mergedPayload);

  // If resolvedPath is empty or root '/' or incomplete overview, handle gracefully
  if (!resolvedPath || resolvedPath === '/') {
    if (routePattern.startsWith('/crm-contacts/overview')) {
      resolvedPath = '/crm-contacts';
    } else if (routePattern.startsWith('/reminders/import')) {
      resolvedPath = '/reminders/import';
    } else if (routePattern.startsWith('/whatsapp-chat')) {
      resolvedPath = '/whatsapp-chat';
    } else {
      resolvedPath = '/dashboard';
    }
  }

  // Safety: If overview path has no contact_id, navigate to contacts list instead of broken overview
  if (
    resolvedPath.startsWith('/crm-contacts/overview') &&
    !resolvedPath.includes('contact_id=')
  ) {
    resolvedPath = '/crm-contacts';
  }

  return resolvedPath;
}

export function buildNotificationNavigation(notification) {
  const resolvedPath = resolveNotificationRoute(notification) || '/dashboard';
  return {
    path: resolvedPath,
    state: {
      fromNotification: true,
      from: getNotificationParentPath(resolvedPath),
    },
  };
}
